/**
 * Single source of truth for the app's neutral / surface colors.
 *
 * Every token has a light and a dark value. Both sets are published as CSS
 * custom properties (`--ev-<token>`) on <html> by ThemeModeProvider, so styles
 * written anywhere (makeStyles, inline `style`, theme overrides) can reference
 * a color with `var(--ev-<token>)` and follow the active mode automatically.
 *
 * Light values are the colors the app already used, so light mode is
 * visually unchanged. Brand accents (coral / orange gradient) and status
 * colors (green, amber, blue…) read fine on both backgrounds and are not
 * tokenised, except for their pale "tint" backgrounds and the dark text that
 * sits on them.
 */

export type ThemeMode = 'light' | 'dark';

export interface ColorTokens {
  // Surfaces
  bgPage: string; // app canvas behind everything
  bgBody: string; // main content area / top bar
  bgNav: string; // sidebar
  bgSurface: string; // cards, papers, dialogs, inputs
  bgSubtle: string; // table heads, zebra rows, muted panels
  bgRaised: string; // popovers, hovered rows, chips on a surface
  chip: string; // default (uncoloured) MUI chip / contained button fill
  // Overlays & lines
  hover: string; // hover fill for rows / list items
  border: string; // hairlines, dividers, card outlines
  borderStrong: string; // input outlines, emphasised separators
  // Text
  textPrimary: string;
  textStrong: string; // slightly softer than primary (headings in panels)
  textSecondary: string;
  textMuted: string;
  // Soft brand tints (MUI primary/secondary `light`)
  primaryLight: string;
  secondaryLight: string;
  divider: string; // MUI palette.divider
  // Pale status tints (backgrounds) and the text that sits on them
  tintAmber: string;
  tintGreen: string;
  tintBlue: string;
  tintOrange: string;
  tintRed: string;
  tintPurple: string;
  onAmber: string;
  onGreen: string;
  onBlue: string;
  onRed: string;
  onPurple: string;
  onOrange: string;
  // Brand & supporting text
  brand: string; // brand coral: solid fills, accents, badges
  brandStrong: string; // deeper brand pink: links, active marks on dashboards
  brandTint: string; // brand wash behind icons / selected items
  brandTintSoft: string; // fainter brand wash (unread rows, hovered list items)
  onBrand: string; // text / icons on a solid brand fill
  textFaint: string; // captions, secondary labels
  textDisabled: string; // placeholders, empty-state icons, timestamps
  iconMuted: string; // neutral toolbar / chevron icons
  // Misc
  shadow: string; // rgb triplet-free shadow colour
  scrim: string; // modal backdrop
  // Auth screens (login / register)
  authBase: string; // base fill of the auth page (under the glows)
  authGlowEdge: string; // pink wash along the left edge of the auth page
  authGlowCenter: string; // soft pink halo between the photo card and the form
  authOutlineBorder: string; // outline of the secondary (Google) button
  authInputBg: string; // fill of auth-screen inputs
  authHeading: string; // fill of the auth heading text (gradient or solid)
  brandGradient: string; // brand coral gradient (headings, accents)
  imageScrim: string; // gradient laid over the hero photo for legibility
  heroRimLight: string; // bright end of the red-tinted glass-edge highlight on the hero card
  heroRimDim: string; // faint end of that highlight
  onImage: string; // text that sits on the hero photo
  heroGradient: string; // brand gradient behind the cut-out hero photo
  // Dashboard / app shell
  navActiveBg: string; // fill of the selected sidebar item
  navActiveText: string; // label of the selected sidebar item
  navActiveIcon: string; // icon of the selected sidebar item
  navItemText: string; // label + icon of an unselected sidebar item
  navItemActiveBg: string; // pill behind the selected sidebar item
  navItemActive: string; // label + icon of the selected sidebar item
  statGradient: string; // fill of the highlighted (hero) stat tile
  onStat: string; // text / icon chip on the hero stat tile
  onStatSoft: string; // decorative marks (bars) on the hero stat tile
  onStatUp: string; // upward trend on the hero stat tile
  onStatDown: string; // downward trend on the hero stat tile
  progressTrack: string; // empty part of a progress bar
  progressFill: string; // filled part of a progress bar (brand red)
  progressGood: string; // filled part of a positive / score bar (green)
  accentPurple: string; // secondary accent (sparkline, pills)
  accentGreen: string; // solid green: fills, icons, positive figures
  accentAmber: string; // solid amber: fills, icons, warnings
  accentBlue: string; // solid blue: fills, icons, links
  accentRed: string; // solid red: fills, icons, errors
  accentOrange: string; // solid orange: fills, icons, secondary brand
  ringTrack: string; // unfilled part of a ring gauge
  ringBlueFrom: string; // ring gradient (blue tone): start
  ringBlueTo: string; // ring gradient (blue tone): end, also dot + label
  ringOrangeFrom: string; // ring gradient (orange tone): start
  ringOrangeTo: string; // ring gradient (orange tone): end, also dot + label
}

export const lightTokens: ColorTokens = {
  bgPage: '#f8f7f5',
  bgBody: '#ffffff',
  bgNav: '#ffffff',
  bgSurface: '#ffffff',
  bgSubtle: '#f8f7f5',
  bgRaised: '#f3f4f6',
  chip: '#e0e0e0',
  hover: 'rgba(0,0,0,0.04)',
  border: 'rgba(0,0,0,0.07)',
  borderStrong: 'rgba(0,0,0,0.15)',
  textPrimary: '#1f2025',
  textStrong: '#374151',
  textSecondary: '#5a5e6b',
  textMuted: '#6b7280',
  primaryLight: '#f3e4db',
  secondaryLight: '#fde8d4',
  divider: 'rgba(0,0,0,0.12)',
  tintAmber: '#fef3c7',
  tintGreen: '#dcfce7',
  tintBlue: '#eff6ff',
  tintOrange: '#fff7ed',
  tintRed: '#fef2f2',
  tintPurple: '#f5f3ff',
  onAmber: '#92400e',
  onGreen: '#15803d',
  onBlue: '#1d4ed8',
  onRed: '#b91c1c',
  onPurple: '#7c3aed',
  onOrange: '#9a3412',
  brand: '#fe3a6a',
  brandStrong: '#e72c6c',
  brandTint: 'rgba(254,58,106,0.10)',
  brandTintSoft: 'rgba(254,58,106,0.05)',
  onBrand: '#ffffff',
  textFaint: '#9ca3af',
  textDisabled: '#c4c8d0',
  iconMuted: '#8a8f99',
  shadow: 'rgba(0,0,0,0.06)',
  scrim: 'rgba(0,0,0,0.5)',
  authBase: 'var(--ev-bgBody)',
  authGlowEdge: 'rgba(254,58,106,0.14)',
  authGlowCenter: 'rgba(236,72,153,0.16)',
  authOutlineBorder: 'rgba(0,0,0,0.18)',
  authInputBg: 'var(--ev-bgSurface)',
  authHeading: 'var(--ev-brandGradient)',
  brandGradient:
    'linear-gradient(90deg, #fe3a6a 0%, #ff4a39 50%, #fe8c45 100%)',
  imageScrim:
    'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.72) 100%)',
  heroRimLight: 'transparent',
  heroRimDim: 'transparent',
  onImage: '#ffffff',
  heroGradient:
    'linear-gradient(115deg, #efd3c8 0%, #ebc9d1 55%, #e7c0d8 100%)',
  navActiveBg: 'rgba(254,58,106,0.08)',
  navActiveText: '#e72c6c',
  navActiveIcon: '#e72c6c',
  navItemText: '#6b7280',
  navItemActiveBg: 'rgba(254,58,106,0.08)',
  navItemActive: '#fe3a6a',
  statGradient: 'linear-gradient(120deg, #fe3a6a 0%, #fe8c45 100%)',
  onStat: '#ffffff',
  onStatSoft: 'rgba(255,255,255,0.88)',
  onStatUp: '#b6f5d2',
  onStatDown: '#ffe0e0',
  progressTrack: 'rgba(0,0,0,0.07)',
  progressFill: '#fe3a6a',
  progressGood: '#10b981',
  accentPurple: '#8b5cf6',
  accentGreen: '#10b981',
  accentAmber: '#f59e0b',
  accentBlue: '#3b82f6',
  accentRed: '#ef4444',
  accentOrange: '#fe8c45',
  ringTrack: '#e5e7eb',
  ringBlueFrom: '#2f6fdc',
  ringBlueTo: '#3a93b8',
  ringOrangeFrom: '#f97316',
  ringOrangeTo: '#e8622c',
};

export const darkTokens: ColorTokens = {
  bgPage: '#111729',
  bgBody: '#111729',
  bgNav: '#0e1424',
  bgSurface: '#171f36',
  bgSubtle: '#1c2540',
  bgRaised: '#222c4a',
  chip: '#2b3556',
  hover: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.18)',
  textPrimary: '#f1f3f9',
  textStrong: '#dfe3ef',
  textSecondary: '#a6aec6',
  textMuted: '#8c95b0',
  primaryLight: '#ff7a98',
  secondaryLight: '#ffb27e',
  divider: 'rgba(255,255,255,0.08)',
  tintAmber: 'rgba(245,158,11,0.16)',
  tintGreen: 'rgba(16,185,129,0.16)',
  tintBlue: 'rgba(59,130,246,0.16)',
  tintOrange: 'rgba(254,140,69,0.16)',
  tintRed: 'rgba(239,68,68,0.16)',
  tintPurple: 'rgba(139,92,246,0.18)',
  onAmber: '#fbbf24',
  onGreen: '#4ade80',
  onBlue: '#7cb4ff',
  onRed: '#fca5a5',
  onPurple: '#c4b5fd',
  onOrange: '#fdba74',
  brand: '#fe3a6a',
  brandStrong: '#ff5c8a',
  brandTint: 'rgba(254,58,106,0.18)',
  brandTintSoft: 'rgba(254,58,106,0.09)',
  onBrand: '#ffffff',
  textFaint: '#7d87a6',
  textDisabled: '#4f5a7c',
  iconMuted: '#8c95b0',
  shadow: 'rgba(0,0,0,0.1)',
  scrim: 'rgba(5,8,18,0.72)',
  authBase: '#0c0c0c',
  authGlowEdge: 'rgba(254,58,106,0.10)',
  authGlowCenter: 'rgba(236,72,153,0.07)',
  authOutlineBorder: 'rgba(255,255,255,0.10)',
  authInputBg: '#2e2e2e',
  authHeading: 'linear-gradient(var(--ev-textPrimary), var(--ev-textPrimary))',
  brandGradient:
    'linear-gradient(90deg, #fe3a6a 0%, #ff4a39 50%, #fe8c45 100%)',
  imageScrim:
    'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(5,8,18,0.82) 100%)',
  heroRimLight: 'rgba(255,125,140,0.55)',
  heroRimDim: 'rgba(254,70,90,0.20)',
  onImage: '#ffffff',
  heroGradient: 'linear-gradient(135deg, #fe3a6a 0%, #fe8c45 100%)',
  navActiveBg: 'rgba(124,92,255,0.18)',
  navActiveText: '#f0a6e4',
  navActiveIcon: '#ff6b57',
  navItemText: '#7d87a6',
  navItemActiveBg: 'rgba(255,255,255,0.12)',
  navItemActive: '#fe3a6a',
  statGradient: 'linear-gradient(120deg, #fe3a6a 0%, #fe8c45 100%)',
  onStat: '#ffffff',
  onStatSoft: 'rgba(255,255,255,0.88)',
  onStatUp: '#b6f5d2',
  onStatDown: '#ffe0e0',
  progressTrack: 'rgba(255,255,255,0.08)',
  progressFill: '#fe3a6a',
  progressGood: '#34d399',
  accentPurple: '#a78bfa',
  accentGreen: '#10b981',
  accentAmber: '#f59e0b',
  accentBlue: '#3b82f6',
  accentRed: '#ef4444',
  accentOrange: '#fe8c45',
  ringTrack: '#f1f3f9',
  ringBlueFrom: '#3b82f6',
  ringBlueTo: '#4aa3c7',
  ringOrangeFrom: '#f97316',
  ringOrangeTo: '#ee7a3a',
};

export const tokensByMode: Record<ThemeMode, ColorTokens> = {
  light: lightTokens,
  dark: darkTokens,
};

const varName = (token: keyof ColorTokens) => `--ev-${token}`;

/** `cssVar('bgSurface')` -> `var(--ev-bgSurface)` */
export const cssVar = (token: keyof ColorTokens) => `var(${varName(token)})`;

/** Typed handle for use in TS styles: `colors.bgSurface`. */
export const colors = (
  Object.keys(lightTokens) as Array<keyof ColorTokens>
).reduce(
  (acc, key) => ({ ...acc, [key]: cssVar(key) }),
  {} as Record<keyof ColorTokens, string>,
);

/** CSS text declaring every token for the given mode. */
export const cssVariablesFor = (mode: ThemeMode): string =>
  (Object.keys(tokensByMode[mode]) as Array<keyof ColorTokens>)
    .map((key) => `${varName(key)}:${tokensByMode[mode][key]};`)
    .join('');

/**
 * Translucent version of any CSS colour, including a token var. Use this
 * instead of appending a hex alpha (`${color}18`), which is invalid CSS once
 * `color` is a `var(--ev-…)` reference.
 */
export const withAlpha = (color: string, percent: number): string =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

const VAR_REFERENCE = /var\((--[\w-]+)\)/g;

/**
 * Replaces every `var(--…)` in a CSS value with its computed value from
 * <html>. For consumers that can't read CSS variables, e.g. <canvas>.
 */
export const resolveCssVars = (value: string): string => {
  if (typeof document === 'undefined') return value;
  const style = getComputedStyle(document.documentElement);
  let resolved = value;
  // Tokens may alias other tokens (`authBase: var(--ev-bgBody)`), so repeat.
  for (let i = 0; i < 3 && VAR_REFERENCE.test(resolved); i += 1) {
    VAR_REFERENCE.lastIndex = 0;
    resolved = resolved.replace(
      VAR_REFERENCE,
      (match, name) => style.getPropertyValue(name).trim() || match,
    );
  }
  VAR_REFERENCE.lastIndex = 0;
  return resolved;
};
